import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LiveStreamButton from './components/LiveStreamButton';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Events from './pages/Events';
import Katha from './pages/Katha';
import Gallery from './pages/Gallery';
import DailyMessage from './pages/DailyMessage';
import Donate from './pages/Donate';
import Contact from './pages/Contact';
import Visiting from './pages/Visiting';
import Services from './pages/Services';

// Admin
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import EventsAdmin from './admin/modules/EventsAdmin';
import GalleryAdmin from './admin/modules/GalleryAdmin';
import KathaAdmin from './admin/modules/KathaAdmin';
import ContactsAdmin from './admin/modules/ContactsAdmin';
import HomeAdmin from './admin/modules/HomeAdmin';
import SocialMediaAdmin from './admin/modules/SocialMediaAdmin';
import AshramDetailsAdmin from './admin/modules/AshramDetailsAdmin';
import SevaDonationAdmin from './admin/modules/SevaDonationAdmin';

// Styles
import './styles/App.css';

const HOMEPAGE_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Place",
      "@id": "https://www.shreeramranujadham.org/#ashram",
      "name": "Shree Ram Ranuja Dham Ashram",
      "alternateName": [
        "Shree Ram Ranuja Dham",
        "Shri Ram Ranuja Dham",
        "Ram Ranuja Dham",
        "Ranuja Dham",
        "Ram Ranuja Ashram",
        "Ranuja Ashram",
        "Shree Ram Ranuja Dham Rampar",
        "Shree Ram Ranuja Dham Lathi",
        "Rampar Ashram",
        "Ramdev Pir Ashram",
        "Ramapir Ashram",
        "Ashram in Rampar Lathi",
        "Ashram in Amreli",
        "Spiritual Ashram in Gujarat",
        "Hindu Ashram in Saurashtra",
        "શ્રી રામ રણુજા ધામ",
        "શ્રી રામ રણુજા ધામ આશ્રમ",
        "રામ રણુજા ધામ",
        "રણુજા ધામ",
        "રામ રણુજા આશ્રમ",
        "રણુજા આશ્રમ",
        "રામપર આશ્રમ",
        "લાઠી આશ્રમ",
        "અમરેલી આશ્રમ",
        "સૌરાષ્ટ્ર આશ્રમ",
        "રામદેવ પીર આશ્રમ",
        "રામાપીર આશ્રમ",
        "Ranuja Dham Amreli",
        "Ashram near Lathi Gujarat",
        "Ashram in Lathi",
        "Ranuja Dham near Lathi",
        "Ram Ranuja Dham Amreli",
        "Shree Ram Ranuja Dham Location",
        "Shree Ram Ranuja Dham Directions",
        "Shree Ram Ranuja Dham Temple",
        "Ram Ranuja Dham Mandir",
        "Shree Ram Ranuja Dham Mandir",
        "Ram Ranuja Dham Temple",
        "Shree Ram Ranuja Dham Ashram Temple",
        "Hindu Temple in Rampar",
        "Hindu Temple near Lathi",
        "Hindu Temple in Amreli",
        "Hindu Temple in Gujarat",
        "Hindu Temple in Saurashtra",
        "Spiritual Temple in Gujarat",
        "Temple near Lathi Gujarat",
        "Temple near Rampar",
        "Temple in Amreli District",
        "Ashram and Temple in Gujarat",
        "Hindu Mandir in Rampar",
        "Ramdev Pir Mandir Gujarat",
        "Ramapir Mandir Amreli",
        "Ramapir Mandir near Lathi",
        "Ramdevji Maharaj Temple",
        "શ્રી રામ રણુજા ધામ રામપર",
        "રણુજા ધામ અમરેલી",
        "લાઠી નજીક આશ્રમ",
        "લાઠીમાં આશ્રમ",
        "અમરેલીમાં આશ્રમ",
        "ગુજરાતમાં હિન્દુ આશ્રમ",
        "ગુજરાતમાં આધ્યાત્મિક આશ્રમ",
        "સૌરાષ્ટ્રમાં આશ્રમ",
        "લાઠી નજીક રણુજા ધામ",
        "રામ રણુજા ધામ અમરેલી",
        "શ્રી રામ રણુજા ધામ લોકેશન",
        "શ્રી રામ રણુજા ધામ જવાનો રસ્તો",
        "શ્રી રામ રણુજા ધામ મંદિર",
        "રામ રણુજા ધામ મંદિર",
        "રામ રણુજા ધામ આશ્રમ મંદિર",
        "રામપરમાં હિન્દુ મંદિર",
        "લાઠી નજીક હિન્દુ મંદિર",
        "અમરેલીમાં હિન્દુ મંદિર",
        "ગુજરાતમાં હિન્દુ મંદિર",
        "સૌરાષ્ટ્રમાં હિન્દુ મંદિર",
        "ગુજરાતમાં આધ્યાત્મિક મંદિર",
        "લાઠી નજીક મંદિર",
        "રામપર નજીક મંદિર",
        "અમરેલી જિલ્લામાં મંદિર",
        "ગુજરાતમાં આશ્રમ અને મંદિર",
        "રામપર હિન્દુ મંદિર",
        "રામદેવ પીર મંદિર ગુજરાત",
        "રામાપીર મંદિર અમરેલી",
        "લાઠી નજીક રામાપીર મંદિર",
        "રામદેવજી મહારાજ મંદિર",
        "श्री राम रणुजा धाम आश्रम",
        "श्री राम रणुजा धाम",
        "राम रणुजा धाम",
        "रणुजा धाम",
        "रणुजा आश्रम",
        "गुजरात में आश्रम",
        "अमरेली आश्रम",
        "श्री राम रणुजा धाम मंदिर",
        "राम रणुजा धाम मंदिर",
        "रामपर मंदिर गुजरात",
        "लाठी के पास मंदिर",
        "अमरेली में हिंदू मंदिर",
        "गुजरात में हिंदू मंदिर",
        "सौराष्ट्र में हिंदू मंदिर",
        "गुजरात में धार्मिक स्थल",
        "गुजरात में आध्यात्मिक आश्रम"
      ],
      "description": "Shree Ram Ranuja Dham Ashram, also known as Ram Ranuja Dham or Ranuja Dham, is a spiritual center in Rampar, Lathi Taluka, Amreli, Saurashtra, Gujarat, associated with Pujya Mahant Shree Radhekrishna Shastri Bapu.",
      "url": "https://www.shreeramranujadham.org/",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Lathi–Bhurkhiya Road, Rampar",
        "addressLocality": "Rampar",
        "addressRegion": "Gujarat",
        "postalCode": "365430",
        "addressCountry": "IN"
      },
      "hasMap": "https://www.google.com/maps/place/Shree+Ram+Ranuja+Dham+Ashram/data=!4m2!3m1!1s0x0:0x33324c124b6534af"
    },
    {
      "@type": "Organization",
      "@id": "https://www.shreeramranujadham.org/#organization",
      "name": "Shree Ram Ranuja Dham Ashram",
      "url": "https://www.shreeramranujadham.org/",
      "logo": "https://www.shreeramranujadham.org/logo.jpg",
      "location": { "@id": "https://www.shreeramranujadham.org/#ashram" },
      "sameAs": [
        "https://www.youtube.com/@Radhekrishnabapuofficial",
        "https://www.facebook.com/radhekrishnashastri.bapu",
        "https://www.instagram.com/radhekrishnabapu_official"
      ]
    },
    {
      "@type": "Person",
      "@id": "https://www.shreeramranujadham.org/#bapu",
      "name": "Shree Radhe Krishna Bapu",
      "honorificPrefix": "Pujya Mahant",
      "alternateName": [
        "Shree Radhekrishna Shastri Bapu",
        "Shri Radhekrishna Shastri Bapu",
        "Shree Radhekrishna Bapu",
        "Shree Radhe Krishna Bapu",
        "Radhekrishna Bapu",
        "Radhe Krishna Bapu",
        "Radhekrishna Shastri Bapu",
        "Radhe Krishna Shastri Bapu",
        "Radhe Krishna Shastri",
        "Pujya Mahant Radhekrishna Bapu",
        "Radhekrishna Bapu Ashram",
        "Radhekrishna Bapu Katha",
        "Radhekrishna Bapu Live",
        "Radhekrishna Bapu Rampar",
        "Radhekrishna Bapu Lathi Amreli",
        "શ્રી રાધેકૃષ્ણ શાસ્ત્રી બાપુ",
        "શ્રી રાધેકૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ બાપુ",
        "રાધે કૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ શાસ્ત્રી",
        "પૂજ્ય મહંત શ્રી રાધેકૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ બાપુ આશ્રમ",
        "રાધેકૃષ્ણ બાપુ કથા",
        "રાધેકૃષ્ણ બાપુ લાઈવ",
        "Shri Radhe Krishna Bapu",
        "Radhekrishna Bapu Live Katha",
        "Radhekrishna Bapu Satsang",
        "Radhekrishna Bapu Official",
        "શ્રી રાધે કૃષ્ણ શાસ્ત્રી બાપુ",
        "શ્રી રાધે કૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ બાપુ સત્સંગ",
        "રાધેકૃષ્ણ બાપુ ઓફિશિયલ",
        "રાધેકૃષ્ણ બાપુ લાઈવ કથા",
        "श्री राधेकृष्ण शास्त्री बापू",
        "श्री राधेकृष्ण बापू",
        "राधेकृष्ण बापू",
        "राधेकृष्ण बापू कथा"
      ],
      "description": "Pujya Mahant Shree Radhekrishna Shastri Bapu is a spiritual leader and Kathakar associated with Shree Ram Ranuja Dham Ashram in Rampar, Lathi, Amreli, Gujarat.",
      "url": "https://www.shreeramranujadham.org/shree-radhe-krishna-bapu",
      "sameAs": [
        "https://www.youtube.com/@Radhekrishnabapuofficial",
        "https://www.facebook.com/radhekrishnashastri.bapu",
        "https://www.instagram.com/radhekrishnabapu_official"
      ],
      "affiliation": { "@id": "https://www.shreeramranujadham.org/#organization" },
      "knowsAbout": [
        "Shrimad Bhagwat Katha",
        "Ram Katha",
        "Shiv Katha",
        "Ramdev Pir Katha",
        "Ramapir Katha",
        "Ramdevji Maharaj Katha",
        "Bar Bij Path",
        "Sanatan Dharma",
        "Satsang",
        "Bhajan",
        "Gujarati Satsang",
        "Radhekrishna Bapu Bhajan",
        "Ramapir Bhajan",
        "Ramdev Pir Bhajan",
        "Bar Bij Mahima",
        "Sanatan Dharma Katha",
        "Hindu Spiritual Discourses",
        "Ramapir Darshan",
        "Ramdev Pir Darshan",
        "Ramapir Bhakti",
        "Ramdev Pir Satsang"
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://www.shreeramranujadham.org/#website",
      "url": "https://www.shreeramranujadham.org/",
      "name": "Shree Ram Ranuja Dham Ashram",
      "inLanguage": ["en", "gu"],
      "publisher": { "@id": "https://www.shreeramranujadham.org/#organization" }
    },
    {
      "@type": "WebPage",
      "@id": "https://www.shreeramranujadham.org/#webpage",
      "url": "https://www.shreeramranujadham.org/",
      "name": "Shree Ram Ranuja Dham Ashram | Radhe Krishna Bapu",
      "isPartOf": { "@id": "https://www.shreeramranujadham.org/#website" },
      "about": [
        { "@id": "https://www.shreeramranujadham.org/#ashram" },
        { "@id": "https://www.shreeramranujadham.org/#bapu" }
      ]
    }
  ]
};

const GlobalHead = () => (
  <Helmet>
    <title>Shree Ram Ranuja Dham Ashram | Radhe Krishna Bapu</title>
    <meta name="description" content="Official website of Shree Ram Ranuja Dham Ashram in Rampar, Lathi, Gujarat, under the guidance of Pujya Mahant Shree Radhe Krishna Bapu. Explore Katha, satsang and seva." />
    <link rel="canonical" href="https://www.shreeramranujadham.org/" />
    <meta property="og:title" content="Shree Ram Ranuja Dham Ashram | Radhe Krishna Bapu" />
    <meta property="og:description" content="Official website of Shree Ram Ranuja Dham Ashram in Rampar, Lathi, Gujarat, under the guidance of Pujya Mahant Shree Radhe Krishna Bapu. Explore Katha, satsang and seva." />
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://www.shreeramranujadham.org/" />
    <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
    <meta property="og:site_name" content="Shree Ram Ranuja Dham Ashram" />
    <script type="application/ld+json">{JSON.stringify(HOMEPAGE_JSONLD)}</script>
  </Helmet>
);

/** Guard: redirect to login if not authenticated */
function RequireAdmin() {
  const { admin, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-orange-50">
        <i className="fas fa-spinner fa-spin text-orange-500 text-3xl"></i>
      </div>
    );
  }
  return admin ? <Outlet /> : <Navigate to="/admin/login" replace />;
}

/** Public site chrome (Header + Footer) */
function PublicLayout() {
  return (
    <div className="App font-poppins">
      <ScrollToTop />
      <LiveStreamButton />
      <Header />
      <main className="min-h-screen">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

/** Login page — send already-logged-in admins to dashboard */
function AdminLoginRoute() {
  const { admin, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-orange-50">
        <i className="fas fa-spinner fa-spin text-orange-500 text-3xl"></i>
      </div>
    );
  }
  if (admin) return <Navigate to="/admin/dashboard" replace />;
  return <AdminLogin />;
}

function App() {
  return (
    <Router>
      <GlobalHead />
      <AdminAuthProvider>
        <Routes>
          {/* Admin login (public) */}
          <Route path="/admin/login" element={<AdminLoginRoute />} />

          {/* Protected admin shell */}
          <Route element={<RequireAdmin />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="events" element={<EventsAdmin />} />
              <Route path="gallery" element={<GalleryAdmin />} />
              <Route path="katha" element={<KathaAdmin />} />
              <Route path="contacts" element={<ContactsAdmin />} />
              <Route path="social" element={<SocialMediaAdmin />} />
              <Route path="ashram" element={<AshramDetailsAdmin />} />
              <Route path="seva" element={<SevaDonationAdmin />} />
              <Route path="home" element={<HomeAdmin />} />
            </Route>
          </Route>

          {/* Public website */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/shree-radhe-krishna-bapu" element={<About />} />
            <Route path="/about" element={<Navigate to="/shree-radhe-krishna-bapu" replace />} />
            <Route path="/events" element={<Events />} />
            <Route path="/katha" element={<Katha />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/daily-message" element={<DailyMessage />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/visiting" element={<Visiting />} />
            <Route path="/services" element={<Services />} />
          </Route>
        </Routes>
      </AdminAuthProvider>
    </Router>
  );
}

export default App;
